import {
  ErrorList,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment, useEffect, useState } from 'react'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import RadioButton from 'src/components/RadioButton/RadioButton'
import { OrganizationRoles } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { css } from 'styled-components'

interface RadioItemsProps {
  className?: string
  fieldLabel?: React.ReactNode
  tooltip?: React.ReactElement
  items: React.ReactElement[]
  id?: string
  currentIndex?: number | undefined
  setIndex: (val: number) => void
  setKey?: (val: string) => void
  onClick?: (val: number, role?: OrganizationRoles | undefined) => void
  setHover?: (val: number | undefined) => void
  currentHover?: number | undefined
  alignStart?: boolean
  grayBackground?: boolean
  whiteBackground?: boolean
  lastGrayBackground?: boolean
  horizontalAlign?: boolean
  staticOnHover?: boolean
  gap?: string
  noBorder?: boolean
  centered?: boolean
  errors?: Array<React.ReactElement | string>
  useRadioItemStyle?: boolean
  showGuestRole?: boolean
  isItemDisabled?: (index: number) => boolean
  ariaLabels?: string[]
}

const RadioItems: React.FC<RadioItemsProps> = (props): React.ReactElement => {
  const translate = useTranslate()
  const [showErrors, setShowErrors] = useState(props.errors !== undefined)

  useEffect((): void => {
    setShowErrors(props.errors !== undefined)
  }, [props.errors])

  useEffect((): void => {
    setShowErrors(false)
  }, [props.currentIndex])

  return (
    <div
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      {props.fieldLabel !== undefined && (
        <div
          css={css`
            display: flex;
            align-items: center;
            z-index: 1;
            margin-bottom: 10px;
          `}
        >
          <FieldLabel
            css={css`
              display: flex;
              align-items: center;
              overflow: visible;
              z-index: 1;
            `}
          >
            {props.fieldLabel}
            {props.tooltip !== undefined && props.tooltip}
          </FieldLabel>
        </div>
      )}
      <div
        css={css`
          display: flex;
          flex-direction: ${props.horizontalAlign ? 'row' : 'column'};
          align-items: flex-start;
          gap: ${props.useRadioItemStyle ? '8px' : props.gap ? props.gap : '0'};
          background-color: ${styleConstants.colors.white};

          ${!props.gap &&
          !props.useRadioItemStyle &&
          css`
            border: 1px solid ${styleConstants.colors.gray_medium};
            border-radius: 10px;
          `}

          ${(!!props.whiteBackground || !!props.lastGrayBackground) &&
          css`
            border-radius: 10px;
          `}

        ${props.noBorder &&
          css`
            border: none;
          `}
        `}
      >
        {props.items.map((item, i) => {
          const guestRoleItem =
            props.showGuestRole && i === props.items.length - 1
          const disabled =
            props.isItemDisabled !== undefined && props.isItemDisabled(i)
          return (
            // TODO: refactor padding and find universal solution
            <Fragment key={i}>
              {guestRoleItem && (
                <span
                  css={css`
                    margin-top: 24px;
                    ${getFontCss(
                      14,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {translate(
                    'Assign roles for contractors or temporary employees',
                  )}
                </span>
              )}
              <div
                css={css`
                  display: flex;
                  min-height: ${i === props.items.length - 1 ||
                  props.horizontalAlign
                    ? 'none'
                    : '50px'}; // convert this to padding
                  align-self: stretch;
                  align-items: center;
                  border-radius: ${i === 0 || i === props.items.length - 1
                    ? 'inherit'
                    : '0px'};
                  margin-bottom: ${props.gap || props.useRadioItemStyle
                    ? '0'
                    : '10px'};

                  ${(props.gap || props.useRadioItemStyle) &&
                  css`
                    border: 1px solid ${styleConstants.colors.gray_medium};
                    border-radius: 10px;
                  `}

                  ${(props.gap || props.useRadioItemStyle) &&
                  props.currentIndex === i &&
                  css`
                    border: 1px solid ${themeConstants.colors.cta};
                  `}

                  ${!!props.useRadioItemStyle &&
                  css`
                    padding: 20px;
                  `}

                  ${!disabled &&
                  css`
                    &:hover {
                      cursor: pointer;
                      ${!props.staticOnHover &&
                      css`
                        background-color: ${styleConstants.colors.gray_light};
                        color: ${styleConstants.colors.blue_light};
                      `}
                    }
                  `}

                  ${!!props.alignStart &&
                  css`
                    align-items: flex-start;
                  `}

                  ${props.currentHover === i &&
                  css`
                    cursor: pointer;
                    background-color: ${styleConstants.colors.gray_light};
                  `}

                  ${!!props.grayBackground &&
                  css`
                    padding: 20px;
                    background-color: ${styleConstants.colors.gray_light};
                  `}

                  ${!!props.whiteBackground &&
                  css`
                    padding: 20px;
                    background-color: ${styleConstants.colors.white};
                  `}

                  ${!!props.lastGrayBackground &&
                  css`
                    padding: 20px;
                    color: red;

                    &:last-child {
                      border-radius: 0px 0px 10px 10px;
                      background-color: ${styleConstants.colors.gray_light};
                      border-top: 1px solid ${styleConstants.colors.gray_medium};
                    }

                    &:nth-child(n + 2) {
                      margin-bottom: 0px;
                    }
                  `}

                  ${props.noBorder &&
                  css`
                    border: none;
                  `}
                `}
                onClick={(): void => {
                  if (disabled) return
                  props.setKey &&
                    typeof item.key === 'string' &&
                    props.setKey(item.key)
                  props.setIndex(i)
                  props.onClick && props.onClick(i)
                }}
                onKeyPress={(): void => {
                  if (disabled) return
                  props.setKey &&
                    typeof item.key === 'string' &&
                    props.setKey(item.key)
                  props.setIndex(i)
                  props.onClick && props.onClick(i)
                }}
                onMouseEnter={(): void => {
                  if (disabled) return
                  if (props.setHover) props.setHover(item.props.highlightIndex)
                }}
                onMouseLeave={(): void => {
                  if (disabled) return
                  if (props.setHover) props.setHover(undefined)
                }}
                onFocus={(): void => {
                  if (disabled) return
                  if (props.setHover) props.setHover(item.props.highlightIndex)
                }}
                role="button"
                tabIndex={0}
                key={i}
              >
                <RadioButton
                  id={props.id}
                  css={css`
                    margin-right: ${props.useRadioItemStyle
                      ? '12px'
                      : props.horizontalAlign
                        ? '5px'
                        : '20px'};
                  `}
                  isSelected={props.currentIndex === i}
                  isHovered={props.currentHover === i}
                  disabled={disabled}
                  showErrors={showErrors}
                  aria-label={props.ariaLabels?.[i] ?? translate('Option')}
                />
                {item}
              </div>
            </Fragment>
          )
        })}
      </div>
      {props.errors !== undefined && showErrors && (
        <ErrorList errors={props.errors} />
      )}
    </div>
  )
}

export default RadioItems
